import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { SimCard } from '../pages/sims/SimManagement'; // Assuming SimCard type is exported or redefine needed fields

type ClusterOption = {
    id: string;
    name: string;
};

type ReassignSimModalProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  sim: SimCard | null; // Pass the SIM to be reassigned
};

export function ReassignSimModal({ open, onClose, sim }: ReassignSimModalProps) {
  const [availableClusters, setAvailableClusters] = useState<ClusterOption[]>([]);
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [fetchingClusters, setFetchingClusters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailableClusters = useCallback(async (orgId: string, currentClusterId: string | null) => {
    if (!orgId) return;
    setFetchingClusters(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('clusters')
        .select('id, name')
        .eq('organization_id', orgId)
        .order('name');

      if (fetchError) throw fetchError;
      
      // Filter out the current cluster
      const filteredClusters = (data || []).filter(c => c.id !== currentClusterId);
      setAvailableClusters(filteredClusters);
      setSelectedClusterId(''); // Reset selection when clusters load

    } catch (err: any) {
      console.error('Error fetching available clusters:', err);
      setError(err.message || 'Failed to load clusters.');
      setAvailableClusters([]);
    } finally {
      setFetchingClusters(false);
    }
  }, []);

  useEffect(() => {
    if (open && sim) {
      // Fetch clusters when modal opens with a valid SIM
      fetchAvailableClusters(sim.organization_id, sim.cluster_id);
    } else {
      // Reset state when modal closes or sim is null
      setAvailableClusters([]);
      setSelectedClusterId('');
      setLoading(false);
      setFetchingClusters(false);
      setError(null);
    }
  }, [open, sim, fetchAvailableClusters]);

  const handleSave = async () => {
    if (!sim || !selectedClusterId) {
      setError('Please select a new cluster.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('sim_cards')
        .update({ cluster_id: selectedClusterId, updated_at: new Date().toISOString() })
        .eq('id', sim.id);

      if (updateError) throw updateError;
      onClose(true); // Close and signal refresh
    } catch (err: any) {
      console.error('Error reassigning SIM:', err);
      setError(err.message || 'Failed to reassign SIM.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Reassign SIM</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sim && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reassigning SIM: {sim.iccid} {sim.msisdn ? `(${sim.msisdn})` : ''}<br/>
            Current Cluster: {sim.clusters?.name || 'Unassigned'}
          </Typography>
        )}
        <Box component="form" noValidate sx={{ mt: 1 }}>
           <TextField
            select
            required
            fullWidth
            margin="dense"
            id="targetCluster"
            name="targetCluster"
            label="New Cluster"
            value={selectedClusterId}
            onChange={(e) => setSelectedClusterId(e.target.value)}
            disabled={fetchingClusters || availableClusters.length === 0}
            helperText={fetchingClusters ? "Loading available clusters..." : (availableClusters.length === 0 ? "No other clusters available." : "Select the cluster to move this SIM to.")}
          >
            <MenuItem value="" disabled><em>Select New Cluster</em></MenuItem>
            {availableClusters.map((cluster) => (
              <MenuItem key={cluster.id} value={cluster.id}>
                {cluster.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || fetchingClusters || !selectedClusterId}
        >
          {loading ? <CircularProgress size={24} /> : 'Reassign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 